import { z } from "zod";

const Pending = z.object({
  status: z.enum(["QUEUED", "RUNNING"]),
  job_id: z.string().min(1),
  retry_after_ms: z.number().int().min(250).max(5000),
});
export interface FiarCatalogOptions {
  readonly signal?: AbortSignal;
  readonly retry?: boolean;
}

/** Only the small catalog-status requests run in HTTP; Snowflake runs in the worker. */
export async function pollFiarCatalog(
  request: (signal: AbortSignal, retry: boolean) => Promise<unknown>,
  options: FiarCatalogOptions = {},
): Promise<unknown> {
  const deadline = Date.now() + 6 * 60_000;
  let retry = options.retry ?? false;
  let jobId: string | undefined;
  while (Date.now() < deadline) {
    options.signal?.throwIfAborted();
    const controller = new AbortController();
    const abort = (): void => controller.abort(options.signal?.reason);
    options.signal?.addEventListener("abort", abort, { once: true });
    if (options.signal?.aborted) abort();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 20_000);
    let payload: unknown;
    try {
      payload = await request(controller.signal, retry);
    } catch (error) {
      if (timedOut && !options.signal?.aborted)
        throw new Error("Product discovery request timed out. Retry product discovery.", {
          cause: error,
        });
      throw error;
    } finally {
      clearTimeout(timer);
      options.signal?.removeEventListener("abort", abort);
    }
    retry = false;
    const pending = Pending.safeParse(payload);
    if (!pending.success) return payload; // Caller validates the successful catalog contract.
    if (jobId !== undefined && jobId !== pending.data.job_id)
      throw new Error("Product discovery changed unexpectedly. Retry product discovery.");
    jobId = pending.data.job_id;
    await waitForCatalog(pending.data.retry_after_ms, options.signal);
  }
  throw new Error("FIAR product discovery is taking too long. Retry product discovery.");
}
function waitForCatalog(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const abort = (): void => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", abort);
      const reason: unknown = signal?.reason;
      reject(reason instanceof Error ? reason : new Error("Product discovery cancelled."));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", abort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", abort, { once: true });
    if (signal?.aborted) abort();
  });
}
